import type {
  LeaveBalance, LeaveRequest, LeaveRequestStatus, LeaveType,
} from '@erp/shared';
import { CalendarDays } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

const STATUS_VARIANT: Record<
  LeaveRequestStatus,
  'info' | 'success' | 'warning' | 'danger' | 'secondary'
> = {
  draft: 'secondary',
  submitted: 'warning',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'secondary',
};

export function LeaveRequestsPage(): JSX.Element {
  const { t } = useTranslation('hr');
  const canApprove = usePermission('hr.leave.approve');
  const canRequest = usePermission('hr.self.leave.request');
  const [requests, setRequests] = useState<LeaveRequest[] | null>(null);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [showCreate, setShowCreate] = useState(false);

  const reload = async (): Promise<void> => {
    const [reqRes, balRes, typesRes] = await Promise.all([
      api().get<{ items: LeaveRequest[] }>('/hr/leave-requests'),
      api().get<{ items: LeaveBalance[] }>('/hr/leave-balances'),
      api().get<{ items: LeaveType[] }>('/hr/leave-types'),
    ]);
    setRequests(reqRes.data.items);
    setBalances(balRes.data.items);
    setTypes(typesRes.data.items);
  };

  useEffect(() => { void reload(); }, []);

  if (!requests) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const typeName = (id: string): string => types.find((tp) => tp.id === id)?.name ?? '?';

  /**
   * All three actions follow the same shape:
   *   1. window.confirm — the action shouldn't happen by accident.
   *   2. (approve/reject only) window.prompt — optional note. null →
   *      operator cancelled the prompt → abort. Empty string → send no
   *      note. Any other value → POST as { note }.
   *   3. POST to the appropriate /leave-requests/:id/<action> endpoint.
   *   4. On error, surface a translated alert instead of swallowing
   *      it like the previous fire-and-forget calls did.
   *
   * Matches the confirm/prompt UX used by the opportunities Win/Lose
   * flow shipped in 1518a6c.
   */
  const onApprove = async (id: string): Promise<void> => {
    if (!(await confirmDialog({ message: t('leave.actions.confirmApprove') }))) return;
    const note = window.prompt(t('leave.actions.approveNote'));
    if (note === null) return;
    try {
      await api().post(`/hr/leave-requests/${id}/approve`, note.trim() ? { note: note.trim() } : {});
      await reload();
    } catch {
      toast.error(t('leave.errors.actionFailed'));
    }
  };
  const onReject = async (id: string): Promise<void> => {
    if (!(await confirmDialog({ message: t('leave.actions.confirmReject') }))) return;
    const note = window.prompt(t('leave.actions.rejectNote'));
    if (note === null) return;
    try {
      await api().post(`/hr/leave-requests/${id}/reject`, note.trim() ? { note: note.trim() } : {});
      await reload();
    } catch {
      toast.error(t('leave.errors.actionFailed'));
    }
  };
  const onCancel = async (id: string): Promise<void> => {
    if (!(await confirmDialog({ message: t('leave.actions.confirmCancel'), tone: 'danger' }))) return;
    try {
      await api().post(`/hr/leave-requests/${id}/cancel`, {});
      await reload();
    } catch {
      toast.error(t('leave.errors.actionFailed'));
    }
  };

  const columns: DataTableColumn<LeaveRequest>[] = [
    {
      id: 'type',
      header: t('leave.columns.type'),
      sortValue: (r) => typeName(r.leaveTypeId),
      searchValue: (r) => typeName(r.leaveTypeId),
      cellClassName: 'font-medium',
      cell: (r) => typeName(r.leaveTypeId),
    },
    {
      id: 'start',
      header: t('leave.columns.start'),
      sortValue: (r) => r.startDate,
      cellClassName: 'font-mono text-xs',
      cell: (r) => r.startDate,
    },
    {
      id: 'end',
      header: t('leave.columns.end'),
      sortValue: (r) => r.endDate,
      cellClassName: 'font-mono text-xs',
      cell: (r) => r.endDate,
    },
    {
      id: 'days',
      header: t('leave.columns.days'),
      align: 'end',
      sortValue: (r) => Number(r.daysRequested),
      cellClassName: 'font-mono tabular-nums',
      cell: (r) => r.daysRequested,
    },
    {
      id: 'status',
      header: t('leave.columns.status'),
      sortValue: (r) => t(`leave.status.${r.status}`),
      searchValue: (r) => t(`leave.status.${r.status}`),
      cell: (r) => (
        <Badge variant={STATUS_VARIANT[r.status]}>
          {t(`leave.status.${r.status}`)}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      headClassName: 'w-48',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          {r.status === 'submitted' && canApprove && (
            <>
              <Button variant="ghost" size="sm" onClick={() => void onApprove(r.id)}>
                {t('leave.actions.approve')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => void onReject(r.id)}>
                {t('leave.actions.reject')}
              </Button>
            </>
          )}
          {/*
            Cancel is offered for submitted + approved requests
            when the operator has either request or admin
            permission. The server enforces ownership — non-owners
            get a 403 — so showing the button here is safe.
            Previously this was gated on `!canApprove`, which
            hid the option from approvers who also requested
            their own leave (a common case for managers).
          */}
          {(r.status === 'submitted' || r.status === 'approved') && canRequest && (
            <Button variant="ghost" size="sm" onClick={() => void onCancel(r.id)}>
              {t('leave.actions.cancel')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('leave.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('leave.description')}</p>
        </div>
        {canRequest && !showCreate && (
          <Button onClick={() => setShowCreate(true)}>{t('leave.create.title')}</Button>
        )}
      </header>

      {balances.length > 0 && (
        <Card className="p-6">
          <h2 className="mb-3 text-sm font-semibold text-fg-muted">{t('leave.myBalances')}</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {balances.map((b) => (
              <div key={b.id} className="rounded-md border border-border bg-bg-subtle p-3">
                <div className="text-xs font-medium">{typeName(b.leaveTypeId)}</div>
                <div className="mt-1 font-mono text-lg">
                  {(Number(b.entitled) - Number(b.consumed) - Number(b.pending)).toFixed(1)}
                </div>
                <div className="text-[0.7rem] text-fg-subtle">
                  {t('leave.balance.summary', {
                    entitled: b.entitled,
                    consumed: b.consumed,
                    pending: b.pending,
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {showCreate && (
        <CreateLeaveForm
          types={types.filter((tp) => tp.isActive)}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {requests.length === 0 && !showCreate ? (
        <EmptyState
          icon={<CalendarDays className="h-8 w-8" aria-hidden />}
          title={t('leave.empty')}
          description={t('leave.emptyDescription')}
          action={
            canRequest ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('leave.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={requests} rowKey={(r) => r.id} />
        </Card>
      )}
    </div>
  );
}

interface CreateFormProps {
  types: LeaveType[];
  onCancel: () => void;
  onCreated: () => void | Promise<void>;
}

function CreateLeaveForm({ types, onCancel, onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const [leaveTypeId, setLeaveTypeId] = useState(types[0]?.id ?? '');
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api().post('/hr/leave-requests', { leaveTypeId, startDate, endDate, reason: reason || undefined });
      await onCreated();
    } catch (err) {
      const cause = (err as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause;
      if (cause?.code === 'insufficient_balance') setError(t('leave.errors.insufficient'));
      else if (cause?.code === 'no_working_days') setError(t('leave.errors.noWorkingDays'));
      else setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{t('leave.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="l-type" label={t('leave.create.type')} required>
          <select
            id="l-type"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={leaveTypeId}
            onChange={(e) => setLeaveTypeId(e.target.value)}
            required
          >
            {types.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
          </select>
        </FormField>
        <FormField id="l-start" label={t('leave.create.start')} required>
          <Input id="l-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
        </FormField>
        <FormField id="l-end" label={t('leave.create.end')} required>
          <Input id="l-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
        </FormField>
        <FormField id="l-reason" label={t('leave.create.reason')}>
          <Input id="l-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
